import { useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardSection,
  Checkbox,
  Form,
  Heading,
  Image,
  Stack,
  Stepper,
  StepperStep,
  Strong,
} from '@customerio/pluma-components/react';
import {
  getWarehouseDestinationImageSrc,
  getWarehouseDestinationName,
  type WarehouseDataOptionID,
} from '@/features/warehouse-sync';
import type { WarehouseDestination } from '@/services/db/warehouse-sync/entity';
import type { WarehouseDataOptionTerms } from '@/features/warehouse-sync';
import { SelectData, type ExportSelection } from './components/select-data';
import { PROVIDER_FORMS, PROVIDER_INITIAL_CONFIG } from './components/provider-form';
import { useBucketConfigDraft } from './components/provider-forms/use-bucket-config-draft';
import { useOptionTerms } from './hooks';
import { useCreateSync } from './use-create-sync';
import * as styles from './components/create-wizard.css';

const STEPS = ['configure', 'select-data'] as const;
type WizardStep = (typeof STEPS)[number];

const STEP_LABELS = {
  configure: 'Configure bucket',
  'select-data': 'Select data',
} as const satisfies Record<WizardStep, string>;

/** Every export is on for a new sync except `inbound`, which is opt-in. */
const DEFAULT_SELECTION: ExportSelection = {
  deliveries: true,
  contents: true,
  metrics: true,
  subjects: true,
  outputs: true,
  people: true,
  attributes: true,
  campaigns: true,
  broadcasts: true,
  objects: true,
  object_attributes: true,
  events: true,
  inbound: false,
};

type CreatePageProps = {
  workspaceId: string;
  workspaceName: string;
  destination: WarehouseDestination;
  isTwilioEnabled: boolean;
  /** Back out of the wizard, to this destination's config page. */
  onCancel: () => void;
  onCreated: () => void;
};

/**
 * The two-step connect flow: enter bucket credentials, then choose what to
 * export. The credentials are validated against the provider before anything is
 * created, so a bad key is caught before a sync exists.
 */
export default function CreatePage({
  workspaceId,
  workspaceName,
  destination,
  isTwilioEnabled,
  onCancel,
  onCreated,
}: CreatePageProps) {
  const terms = useOptionTerms();
  const destinationName = getWarehouseDestinationName(destination);

  const [step, setStep] = useState<WizardStep>('configure');
  const [selection, setSelection] = useState<ExportSelection>(DEFAULT_SELECTION);
  const [hasConfirmedWorkspace, setHasConfirmedWorkspace] = useState(false);

  const { validate, create, isValidating, isCreating } = useCreateSync({
    workspaceId,
    destination,
    destinationName,
    onCreated,
  });

  const isBusy = isValidating || isCreating;
  const draft = useBucketConfigDraft({
    destination,
    mode: 'create',
    initialConfig: PROVIDER_INITIAL_CONFIG[destination],
    isDisabled: isBusy,
  });

  const ProviderForm = PROVIDER_FORMS[destination];
  const canAdvance = step === 'configure' ? !draft.hasErrors : hasConfirmedWorkspace;

  const nextLabel = nextButtonLabel(step, { isValidating, isCreating });

  const goToSelectData = async () => {
    if (!draft.validateAll()) {
      return;
    }
    // The provider is contacted before anything is created, so bad credentials
    // never leave a half-made sync behind.
    const isValid = await validate({ config: draft.getConfig(), selection });
    if (isValid) {
      setStep('select-data');
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // `isDisabled` on the submit button only stops a click. Implicit submission
    // does not go through the button at all, and this Form does implicit-submit:
    // Enter in the step-1 bucket field fires this handler (measured in Chromium).
    // Step 2 currently renders only toggles and a checkbox, and Chromium does not
    // implicit-submit from those — so the guard is not reachable there today. It
    // stays because `create` must never run without `hasConfirmedWorkspace`, the
    // one gate before a workspace's data lands in someone's bucket: any text
    // field added to step 2, or a browser that submits from a checkbox, would
    // open the path. AGENTS.md requires this guard alongside `isDisabled`.
    if (!canAdvance) {
      return;
    }
    if (step === 'configure') {
      await goToSelectData();
    } else {
      await create({ config: draft.getConfig(), selection });
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <Box className={styles.layout}>
        <Card>
          <CardHeader>
            <Stack alignInline="center" gap="100">
              <Image src={getWarehouseDestinationImageSrc(destination)} alt="" size="icon-v2-xl" />
              <Heading level="3">Sync your data to {destinationName}</Heading>
            </Stack>
          </CardHeader>

          <CardSection>
            <WizardStepper
              current={step}
              isConfigureComplete={draft.hasRequiredFields && !draft.hasErrors}
              hasFieldErrors={draft.hasErrors}
              isBusy={isBusy}
              hasConfirmedWorkspace={hasConfirmedWorkspace}
              onSelect={(id) => void (id === 'select-data' ? goToSelectData() : setStep(id))}
            />

            <Box pt="150">
              {step === 'configure' ? (
                <ProviderForm {...draft.form} />
              ) : (
                <SelectDataStep
                  selection={selection}
                  isTwilioEnabled={isTwilioEnabled}
                  isCreating={isCreating}
                  terms={terms}
                  workspaceName={workspaceName}
                  hasConfirmedWorkspace={hasConfirmedWorkspace}
                  onSelectionChange={(optionId, nextValue) =>
                    setSelection((current) => ({ ...current, [optionId]: nextValue }))
                  }
                  onConfirmChange={() => setHasConfirmedWorkspace((current) => !current)}
                />
              )}
            </Box>
          </CardSection>
        </Card>

        <div className={styles.footer}>
          <Button variant="secondary" onClick={() => (step === 'configure' ? onCancel() : setStep('configure'))}>
            Back
          </Button>

          <Button
            data-test-warehouse-button="next"
            type="submit"
            variant="primary"
            isDisabled={!canAdvance}
            isLoading={isBusy}
          >
            {nextLabel}
          </Button>
        </div>
      </Box>
    </Form>
  );
}

function stepStatus(
  id: WizardStep,
  { isConfigureComplete, hasConfirmedWorkspace }: { isConfigureComplete: boolean; hasConfirmedWorkspace: boolean },
) {
  if (id === 'configure') {
    return isConfigureComplete ? 'complete' : 'incomplete';
  }
  return hasConfirmedWorkspace ? 'complete' : 'incomplete';
}

type SelectDataStepProps = {
  selection: ExportSelection;
  isTwilioEnabled: boolean;
  isCreating: boolean;
  terms: WarehouseDataOptionTerms;
  workspaceName: string;
  hasConfirmedWorkspace: boolean;
  onSelectionChange: (optionId: WarehouseDataOptionID, nextValue: boolean) => void;
  onConfirmChange: () => void;
};

/** The exports to write, plus the workspace confirmation that gates creation. */
function SelectDataStep({
  selection,
  isTwilioEnabled,
  isCreating,
  terms,
  workspaceName,
  hasConfirmedWorkspace,
  onSelectionChange,
  onConfirmChange,
}: SelectDataStepProps) {
  return (
    <>
      <SelectData
        selection={selection}
        isTwilioEnabled={isTwilioEnabled}
        isDisabled={isCreating}
        terms={terms}
        onChange={onSelectionChange}
      />

      <Checkbox
        data-test-warehouse-checkbox="confirm-workspace"
        mt="150"
        label={
          <span>
            Confirm you want the data synced from this workspace: <Strong>{workspaceName}</Strong>
          </span>
        }
        isChecked={hasConfirmedWorkspace}
        onCheckedChange={onConfirmChange}
      />
    </>
  );
}

function nextButtonLabel(
  step: WizardStep,
  { isValidating, isCreating }: { isValidating: boolean; isCreating: boolean },
) {
  if (step === 'configure') {
    return isValidating ? 'Validating…' : 'Validate and select data';
  }
  return isCreating ? 'Creating…' : 'Create and sync data';
}

type WizardStepperProps = {
  current: WizardStep;
  isConfigureComplete: boolean;
  hasFieldErrors: boolean;
  hasConfirmedWorkspace: boolean;
  /** A validation or create in flight; the stepper must not start a second one. */
  isBusy: boolean;
  onSelect: (step: WizardStep) => void;
};

function WizardStepper({
  current,
  isConfigureComplete,
  hasFieldErrors,
  hasConfirmedWorkspace,
  isBusy,
  onSelect,
}: WizardStepperProps) {
  return (
    <div className={styles.stepperRow} data-test-warehouse-sync-stepper>
      <Stepper aria-label="Sync setup steps" layout="fit" size="sm">
        {STEPS.map((id) => (
          <StepperStep
            key={id}
            data-test-stepper-step={STEP_LABELS[id]}
            label={STEP_LABELS[id]}
            status={stepStatus(id, { isConfigureComplete, hasConfirmedWorkspace })}
            isCurrent={current === id}
            // Never disable the step being shown, only one not yet reached.
            isDisabled={id === 'select-data' && hasFieldErrors && current !== id}
            onClick={() => {
              if (id !== current && !isBusy) onSelect(id);
            }}
          />
        ))}
      </Stepper>
    </div>
  );
}
